import React, { useState } from 'react';
import * as m from '../paraglide/messages.js';
import { CloseIcon } from './icons.js';
import {
  readShowCreateScratchAppMenu,
  writeShowCreateScratchAppMenu,
} from './showCreateScratchAppMenu.js';
import {
  readShowDeleteScratchAppMenu,
  writeShowDeleteScratchAppMenu,
} from './showDeleteScratchAppMenu.js';
import { readShowPreviewPathBar, writeShowPreviewPathBar } from './showPreviewPathBar.js';
import { readShowPromptActivityLog, writeShowPromptActivityLog } from './showPromptActivityLog.js';

export type StudioSettingsPanelProps = {
  readonly onClose: () => void;
  readonly onUnauthorized: () => void;
  readonly onShowCreateScratchAppMenuChange?: (enabled: boolean) => void;
  readonly onShowDeleteScratchAppMenuChange?: (enabled: boolean) => void;
  readonly onShowPreviewPathBarChange?: (enabled: boolean) => void;
  readonly onShowPromptActivityLogChange?: (enabled: boolean) => void;
};

function openStudioSettingsDialog(dialog: HTMLDialogElement | null): void {
  if (dialog === null || dialog.open) {
    return;
  }
  if (typeof dialog.showModal === 'function') {
    dialog.showModal();
  } else {
    dialog.setAttribute('open', '');
  }
}

export function StudioSettingsPanel(props: StudioSettingsPanelProps): React.ReactElement {
  const [showCreateApp, setShowCreateApp] = useState(() => readShowCreateScratchAppMenu());
  const [showDeleteApp, setShowDeleteApp] = useState(() => readShowDeleteScratchAppMenu());
  const [showPreviewPathBar, setShowPreviewPathBar] = useState(() => readShowPreviewPathBar());
  const [showPromptActivityLog, setShowPromptActivityLog] = useState(() =>
    readShowPromptActivityLog()
  );

  function onToggleCreateApp(event: React.ChangeEvent<HTMLInputElement>): void {
    const enabled = event.target.checked;
    writeShowCreateScratchAppMenu(enabled);
    setShowCreateApp(enabled);
    props.onShowCreateScratchAppMenuChange?.(enabled);
  }

  function onToggleDeleteApp(event: React.ChangeEvent<HTMLInputElement>): void {
    const enabled = event.target.checked;
    writeShowDeleteScratchAppMenu(enabled);
    setShowDeleteApp(enabled);
    props.onShowDeleteScratchAppMenuChange?.(enabled);
  }

  function onTogglePreviewPathBar(event: React.ChangeEvent<HTMLInputElement>): void {
    const enabled = event.target.checked;
    writeShowPreviewPathBar(enabled);
    setShowPreviewPathBar(enabled);
    props.onShowPreviewPathBarChange?.(enabled);
  }

  function onTogglePromptActivityLog(event: React.ChangeEvent<HTMLInputElement>): void {
    const enabled = event.target.checked;
    writeShowPromptActivityLog(enabled);
    setShowPromptActivityLog(enabled);
    props.onShowPromptActivityLogChange?.(enabled);
  }

  return (
    <dialog
      ref={openStudioSettingsDialog}
      className="studio-settings-dialog"
      aria-labelledby="studio-settings-title"
      onCancel={(event) => {
        event.preventDefault();
        props.onClose();
      }}
    >
      <div className="studio-settings-header">
        <div className="studio-settings-heading">
          <p className="studio-settings-eyebrow">{m.settingsEyebrow()}</p>
          <h2 id="studio-settings-title">{m.settingsTitle()}</h2>
        </div>
        <button
          type="button"
          className="icon-button"
          onClick={props.onClose}
          aria-label={m.settingsClose()}
          title={m.settingsClose()}
        >
          <CloseIcon />
        </button>
      </div>
      <section
        className="studio-settings-card"
        aria-labelledby="studio-settings-preview-path-bar-title"
      >
        <div className="studio-settings-card-copy">
          <h3 id="studio-settings-preview-path-bar-title">{m.settingsPreviewPathBarHeading()}</h3>
          <p className="studio-settings-subtitle">{m.settingsPreviewPathBarSubtitle()}</p>
        </div>
        <label className="studio-settings-toggle">
          <input type="checkbox" checked={showPreviewPathBar} onChange={onTogglePreviewPathBar} />
          <span>{m.settingsPreviewPathBarToggle()}</span>
        </label>
        <p className="studio-settings-lead">{m.settingsPreviewPathBarLead()}</p>
      </section>
      <section
        className="studio-settings-card"
        aria-labelledby="studio-settings-prompt-activity-title"
      >
        <div className="studio-settings-card-copy">
          <h3 id="studio-settings-prompt-activity-title">{m.settingsPromptActivityHeading()}</h3>
          <p className="studio-settings-subtitle">{m.settingsPromptActivitySubtitle()}</p>
        </div>
        <label className="studio-settings-toggle">
          <input
            type="checkbox"
            checked={showPromptActivityLog}
            onChange={onTogglePromptActivityLog}
          />
          <span>{m.settingsPromptActivityToggle()}</span>
        </label>
        <p className="studio-settings-lead">{m.settingsPromptActivityLead()}</p>
      </section>
      <section className="studio-settings-card" aria-labelledby="studio-settings-create-app-title">
        <div className="studio-settings-card-copy">
          <h3 id="studio-settings-create-app-title">{m.settingsCreateAppHeading()}</h3>
          <p className="studio-settings-subtitle">{m.settingsCreateAppSubtitle()}</p>
        </div>
        <label className="studio-settings-toggle">
          <input type="checkbox" checked={showCreateApp} onChange={onToggleCreateApp} />
          <span>{m.settingsCreateAppToggle()}</span>
        </label>
        <p className="studio-settings-lead">{m.settingsCreateAppLead()}</p>
      </section>
      <section className="studio-settings-card" aria-labelledby="studio-settings-delete-app-title">
        <div className="studio-settings-card-copy">
          <h3 id="studio-settings-delete-app-title">{m.settingsDeleteAppHeading()}</h3>
          <p className="studio-settings-subtitle">{m.settingsDeleteAppSubtitle()}</p>
        </div>
        <label className="studio-settings-toggle">
          <input type="checkbox" checked={showDeleteApp} onChange={onToggleDeleteApp} />
          <span>{m.settingsDeleteAppToggle()}</span>
        </label>
        <p className="studio-settings-lead">{m.settingsDeleteAppLead()}</p>
      </section>
    </dialog>
  );
}
